import React, { useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { bool, func, object, string } from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import { PrimaryButton, SavedCardDetails } from '..';
import { createLandownerInsuranceSubscription } from '../../util/api';
import { LANDOWNER_INSURANCE_TERMS_VERSION } from '../../util/landownerInsuranceTerms';
import {
  getCreateSubscriptionUserMessageId,
  getPaymentUserMessage,
  LANDOWNER_INSURANCE_SENTRY_CODES,
  logInsuranceEnrollmentError,
} from '../../util/landownerInsuranceEnrollmentErrors';
import { propTypes } from '../../util/types';
import StripePaymentElement from '../StripeCheckoutForm/StripePaymentElement';
import LandownerInsurancePromoCode from './LandownerInsurancePromoCode';
import {
  formatInsurancePrice,
  getDefaultPaymentMethodCard,
  getDefaultStripePaymentMethodId,
  getLandownerInsuranceBasePrice,
  PAYMENT_METHOD_DEFAULT,
  PAYMENT_METHOD_REPLACE,
} from './landownerInsuranceHelpers';
import css from './LandownerInsurance.module.css';

const EnrollmentPaymentForm = ({
  currentUser,
  intl,
  onEnrollmentComplete,
  onApplyPromoCode,
  promoCodeAdjustment,
  promoCodeError,
  requiresPayment,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSupportError, setPaymentSupportError] = useState(false);
  const [enrollmentSupportError, setEnrollmentSupportError] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    getDefaultStripePaymentMethodId(currentUser) ? PAYMENT_METHOD_DEFAULT : PAYMENT_METHOD_REPLACE
  );

  const defaultCard = getDefaultPaymentMethodCard(currentUser);
  const defaultStripePaymentMethodId = getDefaultStripePaymentMethodId(currentUser);
  const showPaymentElement =
    !defaultStripePaymentMethodId || selectedPaymentMethod === PAYMENT_METHOD_REPLACE;
  const coveragePrice = promoCodeAdjustment?.price ?? getLandownerInsuranceBasePrice();
  const formattedCoveragePrice = formatInsurancePrice(intl, coveragePrice);
  const formattedBasePrice = formatInsurancePrice(intl, getLandownerInsuranceBasePrice());

  const completeEnrollment = async () => {
    setIsConfirming(true);
    setPaymentError(null);
    setPaymentSupportError(false);
    setEnrollmentSupportError(false);

    try {
      // Only validate the Payment Element when a charge is expected. The discount is
      // applied server-side via the promotion code, so the source of truth for whether
      // payment is required is the clientSecret the backend returns below.
      if (requiresPayment && showPaymentElement) {
        if (!stripe || !elements) {
          setPaymentError('Payment form is still loading. Please try again.');
          setIsConfirming(false);
          return;
        }

        const { error: submitError } = await elements.submit();

        if (submitError) {
          const userMessage = getPaymentUserMessage(submitError);
          setPaymentError(userMessage || submitError.message);
          setIsConfirming(false);
          return;
        }
      }

      let subscriptionResponse;

      try {
        subscriptionResponse = await createLandownerInsuranceSubscription({
          paymentMethodId: !showPaymentElement ? defaultStripePaymentMethodId : undefined,
          promoCode: promoCodeAdjustment?.code,
          termsVersion: LANDOWNER_INSURANCE_TERMS_VERSION,
        });
      } catch (error) {
        logInsuranceEnrollmentError({
          error,
          code: LANDOWNER_INSURANCE_SENTRY_CODES.createSubscriptionFailed,
        });
        setEnrollmentSupportError(true);
        setIsConfirming(false);
        return;
      }

      const { clientSecret } = subscriptionResponse || {};

      // No clientSecret means the subscription is already fully covered (e.g. a 100%
      // promo code), so there is nothing to confirm with Stripe.
      if (!clientSecret) {
        onEnrollmentComplete();
        setIsConfirming(false);
        return;
      }

      if (!stripe || (showPaymentElement && !elements)) {
        setPaymentError('Payment form is still loading. Please try again.');
        setIsConfirming(false);
        return;
      }

      const { error } = showPaymentElement
        ? await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required',
          })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: defaultStripePaymentMethodId,
          });

      if (error) {
        const userMessage = getPaymentUserMessage(error);

        if (userMessage) {
          setPaymentError(userMessage);
          setPaymentSupportError(false);
        } else {
          logInsuranceEnrollmentError({
            error,
            code: LANDOWNER_INSURANCE_SENTRY_CODES.paymentFailed,
            extra: { stripeType: error.type, stripeCode: error.code },
          });
          setPaymentError(null);
          setPaymentSupportError(true);
        }

        setIsConfirming(false);
        return;
      }

      onEnrollmentComplete();
    } catch (e) {
      logInsuranceEnrollmentError({
        error: e,
        code: LANDOWNER_INSURANCE_SENTRY_CODES.paymentFailed,
      });
      setPaymentError(null);
      setPaymentSupportError(true);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className={css.paymentWindow}>
      <div className={css.sectionIntro}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id="LandownerInsurance.paymentHeading" />
        </h3>
        <div className={css.supportText}>
          {promoCodeAdjustment ? (
            <FormattedMessage
              id={
                requiresPayment
                  ? 'LandownerInsurance.paymentDescriptionWithAdjustedPrice'
                  : 'LandownerInsurance.enrollmentDescriptionWithAdjustedPrice'
              }
              values={{
                basePrice: formattedBasePrice,
                price: formattedCoveragePrice,
              }}
            />
          ) : (
            <FormattedMessage
              id={
                requiresPayment
                  ? 'LandownerInsurance.paymentDescriptionWithPrice'
                  : 'LandownerInsurance.enrollmentDescriptionWithPrice'
              }
              values={{ price: formattedCoveragePrice }}
            />
          )}
        </div>
      </div>

      {defaultCard ? (
        <SavedCardDetails
          className={css.paymentMethodSelector}
          card={defaultCard}
          onChange={setSelectedPaymentMethod}
        />
      ) : null}

      <div
        className={css.paymentElementContainer}
        style={requiresPayment && showPaymentElement ? undefined : { display: 'none' }}
      >
        <StripePaymentElement
          paymentElementId="landowner-insurance-payment-element"
          showSaveCardCheckbox={false}
          onChange={event => {
            setPaymentError(event?.error?.message || null);
            setPaymentSupportError(false);
            setEnrollmentSupportError(false);
          }}
        />
      </div>

      <LandownerInsurancePromoCode
        className={css.promoCode}
        onApply={onApplyPromoCode}
        promoCodeApplied={!!promoCodeAdjustment}
        promoCodeError={promoCodeError}
      />

      {paymentError ? <div className={css.errorMessage}>{paymentError}</div> : null}
      {enrollmentSupportError ? (
        <div className={css.errorMessage}>
          <FormattedMessage id={getCreateSubscriptionUserMessageId()} />
        </div>
      ) : null}
      {paymentSupportError ? (
        <div className={css.errorMessage}>
          <FormattedMessage id="LandownerInsurance.paymentSupportError" />
        </div>
      ) : null}

      <div className={css.buttonRow}>
        <PrimaryButton
          type="button"
          className={css.enrollButton}
          inProgress={isConfirming}
          disabled={isConfirming || !stripe || !elements}
          onClick={completeEnrollment}
        >
          <FormattedMessage
            id="LandownerInsurance.confirmEnrollment"
            values={{ price: formattedCoveragePrice }}
          />
        </PrimaryButton>
      </div>

      <div className={css.authorizationText}>
        <FormattedMessage id="LandownerInsurance.authorizationText" />
      </div>
    </div>
  );
};

EnrollmentPaymentForm.propTypes = {
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  onApplyPromoCode: func.isRequired,
  onEnrollmentComplete: func.isRequired,
  promoCodeAdjustment: object,
  promoCodeError: string,
  requiresPayment: bool,
};

EnrollmentPaymentForm.defaultProps = {
  currentUser: null,
  promoCodeAdjustment: null,
  promoCodeError: null,
  requiresPayment: true,
};

export default EnrollmentPaymentForm;
